import { useEffect, useState, lazy, Suspense } from "react";
import { FileResponse, getFile, rawFileUrl } from "../lib/api";
import { AskTarget } from "../lib/askMessage";
import ErrorBox from "./ErrorBox";
import CodeViewer from "../viewers/CodeViewer";
import MarkdownViewer from "../viewers/MarkdownViewer";
import CSVViewer from "../viewers/CSVViewer";
import NetCDFViewer from "../viewers/NetCDFViewer";
import BinaryViewer from "../viewers/BinaryViewer";

// Lazy-loaded: the notebook renderer ships in its own chunk and only downloads
// when a reviewer opens a .ipynb, so it costs nothing at initial load (#127).
const NotebookViewer = lazy(() => import("../viewers/NotebookViewer"));

/**
 * Decides which viewer to render based on the type returned by /api/file.
 * Markdown is special-cased: the backend tags it `mode=text, language=markdown`
 * — we route to MarkdownViewer rather than CodeViewer so it renders as prose.
 */
export default function FileViewerHost(props: {
  sessionId: number | null;
  path: string | null;
  /** Select-to-ask (issue #56): route a code selection into the chat. */
  onAskClaude?: (req: AskTarget) => void;
}) {
  const [payload, setPayload] = useState<FileResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!props.sessionId || !props.path) {
      setPayload(null);
      return;
    }
    let cancelled = false;
    setPayload(null);
    setError(null);
    (async () => {
      try {
        const body = await getFile(props.sessionId!, props.path!);
        if (!cancelled) setPayload(body);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.sessionId, props.path]);

  if (!props.path) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-ink-500">
        Select a file from the tree to view it.
      </div>
    );
  }
  if (error)
    return (
      <div className="p-6">
        <ErrorBox title="Couldn't display this file" detail={error} />
      </div>
    );
  if (!payload) return <div className="p-6 text-sm text-ink-500">Loading…</div>;

  switch (payload.mode) {
    case "text":
      if (props.path.toLowerCase().endsWith(".ipynb")) {
        // .ipynb comes back as text/json; route it to the rendered notebook
        // view instead of raw JSON in CodeViewer.
        return (
          <Suspense
            fallback={<div className="p-6 text-sm text-ink-500">Loading notebook…</div>}
          >
            <NotebookViewer content={payload.content} truncated={payload.truncated} />
          </Suspense>
        );
      }
      if (payload.language === "markdown") {
        return <MarkdownViewer markdown={payload.content} />;
      }
      return (
        <CodeViewer
          language={payload.language}
          content={payload.content}
          truncated={payload.truncated}
          // Forward undefined (not a no-op wrapper) when no handler exists,
          // so CodeViewer's no-handler guard actually hides the affordance.
          onAskClaude={
            props.onAskClaude &&
            ((sel) => props.onAskClaude!({ path: props.path!, ...sel }))
          }
        />
      );
    case "csv":
      return <CSVViewer columns={payload.columns} rows={payload.rows} total={payload.total_rows} />;
    case "netcdf":
      return <NetCDFViewer payload={payload} />;
    case "binary": {
      const rawUrl =
        props.sessionId && props.path ? rawFileUrl(props.sessionId, props.path) : "";
      return (
        <BinaryViewer
          mime={payload.mime}
          sizeBytes={payload.size_bytes}
          rawUrl={rawUrl}
        />
      );
    }
  }
}
